/**
 * Scratch & win.
 *
 * Flow: open → the sync worker DRAWS the prize (server-side, once per dealer
 * per round) → the card shows with foil over it → the dealer scratches →
 * `revealedAt` is stamped on their card → the prize stays visible here, with
 * its validity and how to claim it. Old rounds' winnings stay listed below so
 * a dealer can show a cashback they're still entitled to.
 *
 * The prize exists before scratching starts: scratching is theatre, the draw
 * is the worker's. Nothing on this screen can change what was drawn.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { scratchClaimRef, type ScratchCard } from '@mitang/shared';
import { AppBar } from '../../components/AppBar';
import { Spinner } from '../../components/Spinner';
import { useScratchConfig, useMyScratchCard, useMyScratchCards } from '../../hooks/useScratchCard';
import { drawScratchCard, revealScratchCard } from '../../lib/repo';
import { logActivity } from '../../lib/activity/activityLogger';
import { ROUTES } from '../../routes/paths';
import { ScratchSurface } from './ScratchSurface';

const dateFmt = new Intl.DateTimeFormat('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
const formatDay = (ts: number): string => dateFmt.format(new Date(ts));

function isExpired(card: ScratchCard, now = Date.now()): boolean {
  return !!card.expiresAt && card.expiresAt < now;
}

/** Short, human reference for the claim ("mention SW-K7Q2M"); shared with the admin panel. */
const claimRef = (card: ScratchCard): string => scratchClaimRef(card.id);

export function ScratchCardScreen(): JSX.Element {
  const navigate = useNavigate();
  const { config, loading: configLoading } = useScratchConfig();
  const { card: liveCard, loading: cardLoading, reload: reloadCard } = useMyScratchCard(config);
  const history = useMyScratchCards();

  // The card the draw call returned, shown until the (restarted) listener
  // delivers it — so the screen never sits blank between draw and snapshot.
  const [drawnCard, setDrawnCard] = useState<ScratchCard | null>(null);
  const card = liveCard ?? (drawnCard && drawnCard.round === config?.round ? drawnCard : null);

  const [drawing, setDrawing] = useState(false);
  const [drawError, setDrawError] = useState<string | null>(null);
  const [justRevealed, setJustRevealed] = useState(false);
  const [retryTick, setRetryTick] = useState(0);
  const drawAttempted = useRef<string | null>(null);

  const enabled = !!config?.enabled;
  const round = config?.round ?? '';

  // An admin can RESET a card (delete it) so the dealer draws again. If that
  // happens while this screen is open, the listener reports the card gone:
  // forget that we already drew this round so the effect below draws afresh.
  const hadCard = useRef(false);
  useEffect(() => {
    if (liveCard) {
      hadCard.current = true;
    } else if (hadCard.current) {
      hadCard.current = false;
      drawAttempted.current = null;
      setDrawnCard(null);
      setJustRevealed(false);
      setRetryTick((t) => t + 1);
    }
  }, [liveCard]);

  // No card for this round yet → ask the worker to draw one, once per round.
  useEffect(() => {
    if (!enabled || configLoading || cardLoading || card || drawing) return;
    if (drawAttempted.current === round) return;
    drawAttempted.current = round;
    setDrawing(true);
    setDrawError(null);
    drawScratchCard()
      .then((drawn) => {
        setDrawnCard(drawn);
        reloadCard(); // the pre-draw listener may have been denied and terminated
      })
      .catch((e: unknown) => {
        setDrawError(e instanceof Error ? e.message : 'Could not get your card.');
        drawAttempted.current = null; // allow Retry
      })
      .finally(() => setDrawing(false));
  }, [enabled, configLoading, cardLoading, card, drawing, round, retryTick, reloadCard]);

  const retry = () => {
    drawAttempted.current = null;
    setDrawError(null);
    setRetryTick((t) => t + 1); // re-runs the draw effect
  };

  const onReveal = () => {
    if (!card || card.revealedAt) return;
    setJustRevealed(true);
    logActivity('scratch_revealed', {
      round: card.round,
      prizeId: card.prizeId,
      prizeTitle: card.prizeTitle,
      won: card.won ? 1 : 0,
    });
    void revealScratchCard(card).catch(() => undefined);
  };

  const pastWins = history.filter((c) => c.won && c.round !== round && !!c.revealedAt);

  return (
    <div className="flex min-h-full flex-1 flex-col bg-ground">
      <AppBar title="Scratch & Win" />
      <div className="flex-1 overflow-y-auto px-4 pb-8 pt-4">
        {configLoading ? (
          <Spinner label="Loading…" />
        ) : !enabled ? (
          <div className="flex flex-col items-center gap-3 px-6 pt-10 text-center">
            <span className="text-4xl" aria-hidden>
              🎟️
            </span>
            <p className="text-[15px] font-bold text-ink">No scratch card right now</p>
            <p className="text-[13px] leading-relaxed text-slate-500">
              We run scratch &amp; win from time to time — you&apos;ll see a card here on the
              home screen when one is on.
            </p>
            <button type="button" className="btn-secondary mt-2" onClick={() => navigate(ROUTES.home)}>
              Back to products
            </button>
          </div>
        ) : (
          <>
            <div className="mb-3 text-center">
              <h1 className="text-[22px] font-extrabold tracking-tight text-ink">
                {config?.title || 'Scratch & Win'}
              </h1>
              <p className="mt-0.5 text-[12.5px] text-slate-500">
                {config?.subtitle || 'Every dealer gets one card this round. Scratch to see yours!'}
              </p>
            </div>

            {cardLoading || (drawing && !card) ? (
              <div className="rounded-2xl bg-white p-8 shadow-card ring-1 ring-slate-100">
                <Spinner label="Preparing your card…" />
              </div>
            ) : drawError && !card ? (
              <div className="rounded-2xl bg-white p-6 text-center shadow-card ring-1 ring-slate-100">
                <p className="text-[13.5px] font-semibold text-red-700">{drawError}</p>
                <button type="button" className="btn-primary mt-4" onClick={retry}>
                  Try again
                </button>
              </div>
            ) : card ? (
              <>
                {card.revealedAt || justRevealed ? (
                  <PrizeFace card={card} revealed celebrate={justRevealed} />
                ) : (
                  <ScratchSurface onReveal={onReveal}>
                    <PrizeFace card={card} revealed={false} />
                  </ScratchSurface>
                )}

                {card.revealedAt || justRevealed ? (
                  <ClaimPanel card={card} />
                ) : (
                  <p className="mt-3 text-center text-[11.5px] text-slate-400">
                    One card per dealer this round · your prize is already locked in
                  </p>
                )}
              </>
            ) : null}

            {pastWins.length > 0 ? (
              <section className="mt-8">
                <h2 className="mb-2 text-[12px] font-bold uppercase tracking-wide text-slate-400">
                  Your earlier rewards
                </h2>
                <ul className="divide-y divide-slate-100 rounded-2xl bg-white ring-1 ring-slate-100">
                  {pastWins.map((c) => {
                    const expired = isExpired(c);
                    return (
                      <li key={c.id} className="flex items-start justify-between gap-3 px-4 py-3">
                        <div className="min-w-0">
                          <p className={`text-[13.5px] font-bold ${expired ? 'text-slate-400 line-through' : 'text-ink'}`}>
                            {c.prizeTitle}
                          </p>
                          <p className="text-[11.5px] text-slate-500">
                            Won {formatDay(c.revealedAt ?? c.drawnAt)} · ref {claimRef(c)}
                          </p>
                        </div>
                        <span
                          className={`shrink-0 rounded-full px-2 py-0.5 text-[10.5px] font-bold ${
                            c.redeemedAt
                              ? 'bg-emerald-50 text-drop'
                              : expired
                                ? 'bg-slate-100 text-slate-500'
                                : 'bg-amber-50 text-stale'
                          }`}
                        >
                          {c.redeemedAt
                            ? 'Claimed'
                            : expired
                              ? 'Expired'
                              : c.expiresAt
                                ? `Valid till ${formatDay(c.expiresAt)}`
                                : 'Not claimed yet'}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </section>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}

/**
 * The card face. Under the foil it is rendered but hidden; once revealed it is
 * the result. Two looks: a win (brand blue, confetti dots) and a blank (calm
 * grey) — a "better luck" card must never look like a win at a glance.
 */
function PrizeFace({
  card,
  revealed,
  celebrate = false,
}: {
  card: ScratchCard;
  revealed: boolean;
  celebrate?: boolean;
}): JSX.Element {
  const won = card.won;
  return (
    <div
      className={`relative flex aspect-[16/10] w-full flex-col items-center justify-center overflow-hidden rounded-2xl px-6 text-center ${
        won
          ? 'bg-gradient-to-br from-brand via-brand-dark to-[#062f5e] text-white'
          : 'bg-gradient-to-br from-slate-100 to-slate-200 text-slate-700'
      } ${revealed ? 'shadow-card' : ''}`}
      aria-live={revealed ? 'polite' : undefined}
    >
      {won && celebrate ? <Confetti /> : null}
      <p className={`text-[11px] font-extrabold uppercase tracking-[.18em] ${won ? 'text-white/70' : 'text-slate-500'}`}>
        {won ? '🎉 You won' : 'This time'}
      </p>
      <p className={`mt-1.5 text-[26px] font-extrabold leading-tight tracking-tight ${won ? '' : 'text-slate-700'}`}>
        {card.prizeTitle}
      </p>
      {card.prizeDescription ? (
        <p className={`mt-2 max-w-[280px] text-[12.5px] leading-snug ${won ? 'text-white/85' : 'text-slate-500'}`}>
          {card.prizeDescription}
        </p>
      ) : won ? null : (
        <p className="mt-2 text-[12.5px] text-slate-500">Thanks for playing — see you next round!</p>
      )}
      {won && card.expiresAt ? (
        <p className="mt-3 rounded-full bg-white/15 px-3 py-1 text-[11px] font-bold">
          Valid till {formatDay(card.expiresAt)}
        </p>
      ) : null}
    </div>
  );
}

/** What to do now — shown once the card is revealed. */
function ClaimPanel({ card }: { card: ScratchCard }): JSX.Element {
  const navigate = useNavigate();
  if (!card.won) {
    return (
      <div className="mt-4 text-center">
        <button type="button" className="btn-primary" onClick={() => navigate(ROUTES.home)}>
          Back to products
        </button>
      </div>
    );
  }
  const expired = isExpired(card);
  return (
    <div className="mt-4 rounded-2xl bg-white p-4 ring-1 ring-slate-100">
      <p className="text-[12px] font-bold uppercase tracking-wide text-slate-400">How to claim</p>
      {card.redeemedAt ? (
        <p className="mt-1.5 text-[13px] leading-relaxed text-drop">
          ✔ Claimed on {formatDay(card.redeemedAt)}. Enjoy!
        </p>
      ) : expired ? (
        <p className="mt-1.5 text-[13px] leading-relaxed text-slate-500">
          This reward expired on {formatDay(card.expiresAt!)}.
        </p>
      ) : (
        <>
          <p className="mt-1.5 text-[13px] leading-relaxed text-slate-600">
            {card.prizeKind === 'cashback'
              ? 'Mention this reward when you place your next order and our team will apply the cashback on that invoice.'
              : 'Mention this reward when you place your next order and our team will send it along with it.'}
          </p>
          <p className="mt-2 text-[13px] text-slate-600">
            Your reference: <span className="font-extrabold text-ink">{claimRef(card)}</span>
            {card.expiresAt ? (
              <>
                {' '}
                · valid till <span className="font-semibold">{formatDay(card.expiresAt)}</span>
              </>
            ) : null}
          </p>
        </>
      )}
      <button type="button" className="btn-secondary mt-4 w-full" onClick={() => navigate(ROUTES.home)}>
        Back to products
      </button>
    </div>
  );
}

/** A few CSS-only confetti flecks for the win reveal — no library, no canvas. */
function Confetti(): JSX.Element {
  const flecks = Array.from({ length: 18 }, (_, i) => i);
  const colours = ['#ffd166', '#ef476f', '#06d6a0', '#ffffff', '#f4a261'];
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
      {flecks.map((i) => (
        <span
          key={i}
          className="absolute block h-2 w-1.5 rounded-sm opacity-0"
          style={{
            left: `${(i * 53) % 100}%`,
            top: '-8px',
            background: colours[i % colours.length],
            animation: `sw-fall 1.6s ease-out ${(i % 6) * 0.12}s forwards`,
            transform: `rotate(${(i * 37) % 360}deg)`,
          }}
        />
      ))}
      <style>{`@keyframes sw-fall{0%{opacity:0;transform:translateY(0) rotate(0)}10%{opacity:1}100%{opacity:0;transform:translateY(240px) rotate(540deg)}}`}</style>
    </div>
  );
}
